import { A } from "@solidjs/router";
import { createMemo, Show } from "solid-js";
import type {
	Persona,
	Product,
	Questionnaire,
	RelevancyQuestionnaire,
	Report,
	View,
} from "../../api/domain.ts";
import { questionnaireStatus, relevancyStatus } from "../../api/domain.ts";
import { useApi } from "../../api/provider.tsx";
import { ok } from "../../api/result.ts";
import { Async } from "../../components/Async.tsx";
import { Panel } from "../../components/Panel.tsx";
import { Table } from "../../components/Table.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createLoader, loadedValue } from "../../lib/async.ts";
import { formatMoment } from "../../lib/format.ts";
import styles from "../admin/admin.module.scss";
import { statusLabel } from "../admin/questionnaire-status.ts";
import {
	compareHref,
	fillHref,
	newestFirst,
	type OpenItem,
	openItems,
} from "./portal.ts";

/** Everything the Start needs, fetched together so the empty state is decided once. */
type World = {
	questionnaires: Questionnaire[];
	relevancy: RelevancyQuestionnaire[];
	reports: Report[];
	views: View[];
	personas: Persona[];
	products: Product[];
};

/**
 * The customer's first screen: what is still open, then the reports, then the
 * way into the comparison. A customer arrives occasionally and must find these
 * three things without training, so the order is action, result, exploration.
 */
export const CustomerStartPage = () => {
	const api = useApi();
	const i18n = useI18n();

	const world = createLoader<World>(async () => {
		const [questionnaires, relevancy, reports, views, personas, products] =
			await Promise.all([
				api.list("questionnaires"),
				api.list("relevancy-questionnaires"),
				api.list("reports"),
				api.list("views", { lang: i18n.locale() }),
				api.list("personas", { lang: i18n.locale() }),
				api.list("products", { lang: i18n.locale() }),
			]);
		for (const result of [
			questionnaires,
			relevancy,
			reports,
			views,
			personas,
			products,
		]) {
			if (!result.ok) return result;
		}
		return ok({
			questionnaires: questionnaires.ok ? questionnaires.value : [],
			relevancy: relevancy.ok ? relevancy.value : [],
			reports: reports.ok ? reports.value : [],
			views: views.ok ? views.value : [],
			personas: personas.ok ? personas.value : [],
			products: products.ok ? products.value : [],
		});
	});

	const loaded = () => loadedValue(world.value());
	const open = createMemo(() => {
		const value = loaded();
		return value === undefined
			? []
			: openItems(value.questionnaires, value.relevancy);
	});
	const isEmpty = () => {
		const value = loaded();
		return (
			value !== undefined &&
			value.questionnaires.length === 0 &&
			value.relevancy.length === 0 &&
			value.reports.length === 0 &&
			value.personas.length === 0 &&
			value.products.length === 0
		);
	};

	const subject = (item: OpenItem, value: World): string =>
		item.kind === "product"
			? (value.products.find(
					(product) => product.id === item.questionnaire.product,
				)?.name ??
				item.questionnaire.product ??
				"")
			: (value.personas.find(
					(persona) => persona.id === item.questionnaire.persona,
				)?.name ?? item.questionnaire.persona);

	const status = (item: OpenItem): string =>
		statusLabel(
			i18n.t(),
			item.kind === "product"
				? questionnaireStatus(item.questionnaire)
				: relevancyStatus(item.questionnaire),
		);

	return (
		<>
			<div class={styles.heading}>
				<h1>{i18n.t().portal.startTitle}</h1>
			</div>

			<Async value={world.value()} onRetry={world.reload}>
				{(value) => (
					<Show
						when={!isEmpty()}
						fallback={<p class={styles.note}>{i18n.t().portal.empty}</p>}
					>
						<Panel title={i18n.t().portal.reports}>
							<Table
								rows={newestFirst(value.reports)}
								rowKey={(report) => report.id}
								emptyLabel={i18n.t().portal.noReports}
								columns={[
									{
										key: "view",
										header: i18n.t().reports.view,
										cell: (report) => (
											<A href={`/reports/${report.id}`}>
												{value.views.find((view) => view.id === report.view)
													?.label ?? report.view}
											</A>
										),
									},
									{
										key: "asOf",
										header: i18n.t().reports.asOf,
										cell: (report) => formatMoment(report.as_of, i18n.locale()),
									},
									{
										key: "locked",
										header: i18n.t().common.status,
										cell: (report) =>
											report.locked_at === null
												? i18n.t().portal.draft
												: `${i18n.t().portal.lockedOn} ${formatMoment(report.locked_at, i18n.locale())}`,
									},
								]}
							/>
						</Panel>

						<Panel title={i18n.t().nav.compare}>
							<p class={styles.note}>
								{value.personas.length === 0
									? i18n.t().portal.noWeighting
									: i18n.t().portal.compareHint}
							</p>
							<A href={compareHref(value.views)}>{i18n.t().portal.compare}</A>
						</Panel>
					</Show>
				)}
			</Async>
		</>
	);
};
